import { type QuotePackage, adjustmentLineItems } from "./helpers";
import { formatCurrency, formatValueDisplay } from "helpers/currency";

/**
 * Price Breakdown Content
 * Shared component for displaying price breakdown in package cards
 */
export default function PriceBreakdownContent({ pkg }: { pkg: QuotePackage }) {
  // Get selected additional services (those with quantity > 0 or fee > 0)
  const selectedAddOns = (pkg.breakdown.additional_services || []).filter(
    (service) =>
      (service.total_quantity ?? 0) > 0 || (service.total_fee ?? 0) > 0,
  );

  const items = [
    {
      label: "Package",
      displayValue: formatCurrency(pkg.breakdown.package_price),
    },
    pkg.breakdown.death_certificates?.total_fee > 0 &&
      pkg.breakdown.death_certificates?.total_quantity > 0 && {
        label: "Death Certificates",
        displayValue: formatValueDisplay(
          pkg.breakdown.death_certificates.total_quantity,
          Math.round(
            pkg.breakdown.death_certificates.total_fee /
              pkg.breakdown.death_certificates.total_quantity,
          ),
          pkg.breakdown.death_certificates.total_fee,
        ),
      },
    pkg.breakdown.weight_fee?.total_fee > 0 && {
      label: "Weight fee",
      displayValue: formatCurrency(pkg.breakdown.weight_fee.total_fee),
    },
    pkg.breakdown.transport_fee?.total_fee > 0 && {
      label: "Transportation",
      displayValue: formatCurrency(pkg.breakdown.transport_fee.total_fee),
    },
    pkg.breakdown.delivery_fee?.total_fee > 0 && {
      label: "Delivery",
      displayValue: formatCurrency(pkg.breakdown.delivery_fee.total_fee),
    },
    pkg.breakdown.pacemaker_fee?.total_fee > 0 && {
      label: "Pacemaker removal",
      displayValue: formatCurrency(pkg.breakdown.pacemaker_fee.total_fee),
    },
    pkg.breakdown.fingerprint_fee?.total_fee > 0 && {
      label: "Fingerprints",
      displayValue: formatCurrency(pkg.breakdown.fingerprint_fee.total_fee),
    },
    pkg.breakdown.inurnment_fee?.total_fee > 0 && {
      label: "Inurnment",
      displayValue: formatValueDisplay(
        pkg.breakdown.inurnment_fee.total_quantity,
        pkg.breakdown.inurnment_fee.unit_price,
        pkg.breakdown.inurnment_fee.total_fee,
      ),
    },
    pkg.breakdown.storage?.total_fee &&
      pkg.breakdown.storage.total_fee > 0 &&
      pkg.breakdown.storage.total_quantity > 0 && {
        label: "Storage",
        displayValue: `${pkg.breakdown.storage.total_quantity} days @ ${formatCurrency(Math.round(pkg.breakdown.storage.total_fee / pkg.breakdown.storage.total_quantity))}`,
      },
    ...(pkg.breakdown.state_fees || []).map((fee) => ({
      label: fee.name,
      displayValue: formatCurrency(fee.amount),
    })),
    ...(pkg.breakdown.county_fees || []).map((fee) => ({
      label: fee.name,
      displayValue: formatCurrency(fee.amount),
    })),
    // Selected additional services (add-ons from personalize)
    ...selectedAddOns.map((service) => {
      const qty = service.total_quantity ?? 0;
      const name = service.label || service.name;
      const totalFee = service.total_fee ?? service.amount;
      return {
        label: name,
        displayValue:
          qty > 1
            ? `${qty} @ ${formatCurrency(service.amount)}`
            : formatCurrency(totalFee),
      };
    }),
    ...adjustmentLineItems(pkg.breakdown),
  ].filter(Boolean) as { label: string; displayValue: string }[];

  return (
    <div className="space-y-2 rounded-md py-2 text-sm">
      {items.map((item, idx) => (
        <div key={idx} className="flex justify-between text-evergreen">
          <span>{item.label}</span>
          <span>{item.displayValue}</span>
        </div>
      ))}
      <div className="flex justify-between font-bold text-evergreen">
        <span>Total</span>
        <span>{formatCurrency(pkg.total)}</span>
      </div>
    </div>
  );
}
